// Operations -> Applicants (George 2026-10-31). The Outlook Hiring folder's
// resume tracker, loaded nightly from R2 (services/applicantSync.js). Admin and
// leadership only; the server enforces it, the tab is hidden to match.
// Profile fields come from the feed and are read-only here; Status, Notes and
// Hire -> Roster are CRM's.

import { useEffect, useState, Fragment } from 'react';
import { api } from '../api';
import { styles, colors, fmtDate, fmtDateTime } from './UI';
import { useTableControls, SortableFilterTh, ActiveFiltersBar } from './TableControls';

const CERT_LABELS = {
  osha10: 'OSHA 10', osha30: 'OSHA 30', osha500: 'OSHA 500', osha510: 'OSHA 510', csp: 'CSP', asp: 'ASP',
  chst: 'CHST', ohst: 'OHST', sms: 'SMS', h2s: 'H2S', cpr_first_aid: 'CPR/FA', twic: 'TWIC', nfpa1006: 'NFPA 1006',
  confined_space_rescue: 'CS Rescue', rope_rescue: 'Rope Rescue', irata_sprat: 'IRATA/SPRAT',
  emt_paramedic: 'EMT/Medic', firefighter: 'Firefighter', scba: 'SCBA', fire_watch_hole_watch: 'Fire/Hole Watch',
  instructor_trainer: 'Trainer', bilingual_spanish: 'Bilingual',
};
const STATUS_COLORS = {
  New: [colors.blueSoft, colors.blue], Contacted: [colors.amberSoft, '#92400E'],
  Interviewed: ['#EDE9FE', '#5B21B6'], Hired: [colors.greenSoft, '#065F46'], Passed: [colors.graySoft, colors.textMuted],
};
const certList = (a) => Object.entries(a.certs || {}).filter(([, v]) => v).map(([k]) => CERT_LABELS[k] || k);

const COL_LABELS = {
  name: 'Candidate', group: 'Group', status: 'Status', years: 'Yrs', recent: 'Recent', location: 'Location',
  contact: 'Contact', certs: 'Certs', last: 'Last Email',
};

export default function ApplicantsTab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [open, setOpen] = useState(null);
  const [notes, setNotes] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => api.applicantsList().then(d => { setData(d); setErr(''); }).catch(e => setErr(e.message));
  useEffect(() => { load(); }, []);

  const rows = data ? data.applicants : [];
  const cols = {
    name: a => a.name,
    group: a => [a.primary_group, a.secondary_groups].filter(Boolean).join(', '),
    status: a => a.status,
    years: a => a.years_experience || '',
    recent: a => [a.recent_title, a.recent_employer].filter(Boolean).join(' · '),
    location: a => a.location || '',
    contact: a => [a.phone, a.email].filter(Boolean).join(' '),
    certs: a => certList(a).join(', '),
    last: a => a.last_received || '',
  };
  // Location sorts by state, then city ("Fort Worth, TX" -> "TX Fort Worth"), so a
  // click groups candidates by region. Certs sorts by how many the candidate holds,
  // most first; the column search still finds a specific one ("510", "TWIC").
  const sortCols = {
    years: a => (a.years_experience === '' || a.years_experience == null ? null : Number(a.years_experience)),
    location: a => {
      const parts = (a.location || '').split(',').map(x => x.trim()).filter(Boolean);
      if (!parts.length) return null;
      return parts.length > 1 ? `${parts[parts.length - 1]} ${parts.slice(0, -1).join(', ')}` : parts[0];
    },
    certs: a => { const n = certList(a).length; return n ? String(100 - n).padStart(3, '0') : null; },
  };
  const { displayRows, headerProps, filters, clearFilter, clearAll } = useTableControls(rows, cols, sortCols);

  const act = async (fn, ok) => {
    setBusy(true); setMsg('');
    try { const r = await fn(); setMsg(ok(r)); await load(); }
    catch (e) { setMsg(`⚠ ${e.message}`); }
    finally { setBusy(false); }
  };
  const setStatus = (a, status) => act(() => api.applicantUpdate(a.id, { status }), () => `${a.name}: ${status}`);
  const saveNotes = (a) => act(() => api.applicantUpdate(a.id, { notes: notes[a.id] ?? a.notes ?? '' }),
    () => { setNotes(n => { const c = { ...n }; delete c[a.id]; return c; }); return `${a.name}: notes saved`; });
  const hire = (a) => {
    if (!window.confirm(`Add ${a.name} to the Roster and mark Hired?\n\nAn employee already on the Roster under this name is linked, not duplicated.`)) return;
    act(() => api.applicantHire(a.id), r => r.created ? `${a.name} added to the Roster` : `${a.name} linked to Roster employee ${r.employee_name || ''}`.trim());
  };
  const download = (a, i, f) => api.applicantDownloadFile(a.id, i, f.name).catch(e => setMsg(`⚠ ${e.message}`));
  const sync = () => act(() => api.applicantSync(), r => r.ok
    ? (r.skipped ? 'Feed unchanged since the last load' : `Loaded ${r.loaded}, past retention ${r.skipped_old}, purged ${r.purged}`)
    : `⚠ ${r.error}`);

  if (err) return <div style={styles.errorBox}>{err}</div>;
  if (!data) return <div style={styles.card}>Loading applicants…</div>;
  const s = data.sync || {};
  const counts = (data.statuses || []).map(st => [st, rows.filter(a => a.status === st).length]);
  const searching = Object.values(filters).some(f => f.value);

  return (
    <div style={styles.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
        <div>
          <h2 style={{ margin: 0, color: colors.charcoal }}>
            🧑‍💼 Applicants <span style={{ color: colors.textMuted, fontWeight: 400 }}>· {searching ? `${displayRows.length} of ${rows.length}` : rows.length}</span>
          </h2>
          <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 4 }}>
            From the Outlook Hiring folder, loaded nightly. Kept 90 days after the last email unless Hired. Click a column header to sort.
            {' '}Last loaded {s.last_ok_at ? fmtDateTime(s.last_ok_at) : 'never'}
            {s.report_modified ? ` · feed written ${fmtDateTime(s.report_modified)}` : ''}.
            {s.last_status === 'error' && <span style={{ color: colors.red, fontWeight: 600 }}> Last sync failed: {s.last_error}</span>}
          </div>
        </div>
        <button style={styles.btnSecondary} disabled={busy} onClick={sync}>⟳ Sync now</button>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8, fontSize: 12 }}>
        {counts.map(([st, n]) => (
          <span key={st} style={{ ...styles.pill, background: STATUS_COLORS[st][0], color: STATUS_COLORS[st][1] }}>{st} {n}</span>
        ))}
      </div>
      {msg && <div style={{ fontSize: 13, margin: '6px 0', color: msg.startsWith('⚠') ? colors.red : '#065F46' }}>{msg}</div>}
      <ActiveFiltersBar filters={filters} clearFilter={clearFilter} clearAll={clearAll} colLabels={COL_LABELS} />
      <div style={{ overflowX: 'auto' }}>
        <table style={styles.table}>
          <thead>
            <tr>
              {Object.keys(COL_LABELS).map(k => <SortableFilterTh key={k} compact label={COL_LABELS[k]} {...headerProps(k)} />)}
              <th style={styles.th}>Resume</th>
            </tr>
          </thead>
          <tbody>
            {displayRows.map(a => {
              const isOpen = open === a.id;
              const [bg, fg] = STATUS_COLORS[a.status] || STATUS_COLORS.New;
              return (
                <Fragment key={a.id}>
                  <tr style={{ background: isOpen ? colors.graySoft : undefined }}>
                    <td style={styles.td}>
                      <button onClick={() => setOpen(isOpen ? null : a.id)}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 600, color: colors.charcoal, textAlign: 'left' }}>
                        {isOpen ? '▾' : '▸'} {a.name}
                      </button>
                      {a.resume_status && a.resume_status !== 'read' && (
                        <div style={{ fontSize: 11, color: colors.red, fontWeight: 600 }}>resume not readable, open in Outlook</div>
                      )}
                    </td>
                    <td style={styles.td}>
                      <div>{a.primary_group}</div>
                      {a.secondary_groups && <div style={{ fontSize: 11, color: colors.textMuted }}>{a.secondary_groups}</div>}
                    </td>
                    <td style={styles.td}>
                      <select value={a.status} disabled={busy} onChange={e => setStatus(a, e.target.value)}
                        style={{ ...styles.input, padding: '3px 6px', width: 'auto', background: bg, color: fg, fontWeight: 600 }}>
                        {data.statuses.map(st => <option key={st} value={st}>{st}</option>)}
                      </select>
                    </td>
                    <td style={styles.td}>{a.years_experience}</td>
                    <td style={styles.td}>
                      <div>{a.recent_title}</div>
                      <div style={{ fontSize: 11, color: colors.textMuted }}>{a.recent_employer}</div>
                    </td>
                    <td style={styles.td}>{a.location}</td>
                    <td style={styles.td}>
                      {a.phone && <div><a href={`tel:${a.phone}`}>{a.phone}</a></div>}
                      {a.email && <div style={{ fontSize: 12 }}><a href={`mailto:${a.email}`}>{a.email}</a></div>}
                    </td>
                    <td style={{ ...styles.td, maxWidth: 220 }}>
                      {certList(a).map(c => (
                        <span key={c} style={{ display: 'inline-block', fontSize: 11, padding: '1px 6px', margin: '0 4px 4px 0', borderRadius: 10, background: '#FFF1E8', color: colors.orangeDark, fontWeight: 600 }}>{c}</span>
                      ))}
                    </td>
                    <td style={styles.td}>{a.last_received ? fmtDate(a.last_received) : ''}</td>
                    <td style={styles.td}>
                      {a.files.map((f, i) => (
                        <div key={i}><button onClick={() => download(a, i, f)} title={f.name}
                          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: colors.blue, fontSize: 12, textAlign: 'left' }}>
                          📄 {f.name.length > 28 ? `${f.name.slice(0, 26)}…` : f.name}
                        </button></div>
                      ))}
                      {a.outlook_link && <div><a href={a.outlook_link} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>✉ Outlook</a></div>}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr style={{ background: colors.graySoft }}>
                      <td colSpan={10} style={{ ...styles.td, paddingTop: 0 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 1fr) minmax(260px, 1fr)', gap: 16 }}>
                          <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                            {a.summary && <div style={{ marginBottom: 6 }}>{a.summary}</div>}
                            {a.position_applied && <div><b>Applied for:</b> {a.position_applied}</div>}
                            {a.source && <div><b>Source:</b> {a.source}</div>}
                            {a.industries && <div><b>Industries:</b> {a.industries}</div>}
                            {a.other_certs && <div><b>Other certs:</b> {a.other_certs}</div>}
                            <div><b>Emails:</b> {a.email_count}, first {a.first_received ? fmtDate(a.first_received) : '?'}</div>
                            {a.status_updated_at && <div style={{ color: colors.textMuted }}>Status set {fmtDateTime(a.status_updated_at)}{a.status_updated_by_name ? ` by ${a.status_updated_by_name}` : ''}</div>}
                            <div style={{ color: colors.textMuted, fontSize: 12 }}>Certs are what the resume states, not verified training.</div>
                          </div>
                          <div>
                            <label style={styles.label}>Notes</label>
                            <textarea style={{ ...styles.textarea, minHeight: 70 }} value={notes[a.id] ?? a.notes ?? ''}
                              onChange={e => setNotes(n => ({ ...n, [a.id]: e.target.value }))} />
                            <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                              <button style={styles.btnSecondary} disabled={busy || notes[a.id] === undefined} onClick={() => saveNotes(a)}>
                                Save notes{notes[a.id] !== undefined ? ' •' : ''}
                              </button>
                              {a.employee_id
                                ? <span style={{ fontSize: 13, color: '#065F46', fontWeight: 600 }}>✓ On the Roster as {a.employee_name}</span>
                                : <button style={styles.btn} disabled={busy} onClick={() => hire(a)}>Hire → Roster</button>}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {!displayRows.length && (
              <tr><td colSpan={10} style={{ ...styles.td, textAlign: 'center', color: colors.textMuted }}>
                {rows.length ? 'No applicants match the filters.' : 'No applicants loaded yet. The feed lands after the 2:30 AM saver run.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
